import { createSlice } from "@reduxjs/toolkit";
import {
  signinAuthentication,
  signupAuthentication,
  forgotPasswordAuthentication,
  resetPasswordAuthentication,
  googleSigninAuthentication,
  googleSignupAuthentication,
  linkedInSigninAuthentication,
  linkedInSignupAuthentication,
  accountVerification,
  resendVerificationEmail
} from "./actions";
import { notification } from "antd";
import { dataDecrypt, dataEncrypt } from "../../../helpers/cryption";

const initialState = {
  // Signin states
  signinData: {},
  signinLoading: false,
  signinError: null,

  // Signup states
  signupData: {},
  signupLoading: false,
  signupError: null,

  // Forgot password states
  forgotPasswordData: {},
  forgotPasswordLoading: false,
  forgotPasswordError: null,

  // Reset password states
  resetPasswordData: {},
  resetPasswordLoading: false,
  resetPasswordError: null,

  // Google SSO states
  googleSigninData: {},
  googleSigninLoading: false,
  googleSigninError: null,

  googleSignupData: {},
  googleSignupLoading: false,
  googleSignupError: null,

  // LinkedIn SSO states
  linkedInSigninData: {},
  linkedInSigninLoading: false,
  linkedInSigninError: null,

  linkedInSignupData: {},
  linkedInSignupLoading: false,
  linkedInSignupError: null,

  // Verification states
  verificationData: {},
  verificationLoading: false,
  verificationError: null,

  resendVerificationData: {},
  resendVerificationLoading: false,
  resendVerificationError: null,

};

const authSlice = createSlice({
  name: "authUser",
  initialState,
  reducers: {
    clearSigninData: (state) => {
      state.signinData = {};
    },
    clearSignupData: (state) => {
      state.signupData = {};
    },
    clearForgotPasswordData: (state) => {
      state.forgotPasswordData = {};
    },
    clearResetPasswordData: (state) => {
      state.resetPasswordData = {};
    },
    clearGoogleAuth: (state) => {
      state.googleSigninData = {};
      state.googleSignupData = {};
    },
    clearLinkedInAuth: (state) => {
      state.linkedInSigninData = {};
      state.linkedInSignupData = {};
    },
    clearVerificationData: (state) => {
      state.verificationData = {};
    },
    clearResendVerificationData: (state) => {
      state.resendVerificationData = {};
    },
  },
  extraReducers: (builder) => {
    // Signin reducers
    builder
      .addCase(signinAuthentication.pending, (state) => {
        state.signinLoading = true;
        state.signinError = null;
      })
      .addCase(signinAuthentication.fulfilled, (state, action) => {
        state.signinLoading = false;
        const payload = action.payload;

        if (payload.meta?.status === 'ok') {
          notification.success({
            message: "Success",
            description: payload?.meta?.message,
            duration: 2,
          });

          const encripytUser = dataEncrypt(action.payload.data.user);
          const encripytToken = dataEncrypt(action.payload.data.token);
          localStorage.setItem("HOQ_User", encripytUser);
          localStorage.setItem("HOQ_Token", encripytToken);
          state.signinData = action.payload;


        } else {
          notification.error({
            message: "Error",
            description: payload.meta.message,
            duration: 2,
          });
        }
      })
      .addCase(signinAuthentication.rejected, (state, action) => {
        state.signinLoading = false;
        state.signinError = action.payload;
      })

      // Signup reducers
      .addCase(signupAuthentication.pending, (state) => {
        state.signupLoading = true;
        state.signupError = null;
      })
      .addCase(signupAuthentication.fulfilled, (state, action) => {
        state.signupLoading = false;
        state.signupData = action.payload;
        const payload = action?.payload;



        if (payload?.meta?.status === 'ok') {
          const encripytUser = dataEncrypt(action.payload.data.user);
          const encripytToken = dataEncrypt(action.payload.data.token);
          localStorage.setItem("HOQ_User", encripytUser);
          localStorage.setItem("HOQ_Token", encripytToken);
          notification.success({
            message: "Success",
            description: payload?.meta?.message,
            duration: 2,
          });
        } else {
          notification.error({
            message: "Error",
            description: payload?.meta?.message,
            duration: 2,
          });
        }
      })
      .addCase(signupAuthentication.rejected, (state, action) => {
        state.signupLoading = false;

        state.signupError = action.payload;
      })

      // Forgot password reducers
      .addCase(forgotPasswordAuthentication.pending, (state) => {
        state.forgotPasswordLoading = true;
        state.forgotPasswordError = null;
      })
      .addCase(forgotPasswordAuthentication.fulfilled, (state, action) => {
        state.forgotPasswordLoading = false;
        state.forgotPasswordData = action.payload;
        const payload = action.payload;
        if (payload?.meta?.status === 'ok') {
          notification.success({
            message: "Success",
            description: payload?.meta?.message,
            duration: 2,
          });
        } else {
          notification.error({
            message: "Error",
            description: payload?.meta?.message,
            duration: 2,
          });
        }
      })
      .addCase(forgotPasswordAuthentication.rejected, (state, action) => {
        state.forgotPasswordLoading = false;
        state.forgotPasswordError = action.payload;
      })

      // Reset password reducers
      .addCase(resetPasswordAuthentication.pending, (state) => {
        state.resetPasswordLoading = true;
        state.resetPasswordError = null;
      })
      .addCase(resetPasswordAuthentication.fulfilled, (state, action) => {
        state.resetPasswordLoading = false;
        state.resetPasswordData = action.payload;
        const payload = action.payload;
        if (payload?.meta?.status === 'ok') {
          notification.success({
            message: "Success",
            description: payload?.meta?.message,
            duration: 2,
          });
        } else {
          notification.error({
            message: "Error",
            description: payload?.meta?.message,
            duration: 2,
          });
        }
      })
      .addCase(resetPasswordAuthentication.rejected, (state, action) => {
        state.resetPasswordLoading = false;
        state.resetPasswordError = action.payload;
      });


    builder
      .addCase(googleSigninAuthentication.pending, (state) => {
        state.googleSigninLoading = true;
        state.googleSigninError = null;
      })
      .addCase(googleSigninAuthentication.fulfilled, (state, action) => {
        state.googleSigninLoading = false;
        state.googleSigninData = action.payload;
        if (action?.payload?.meta?.success === true) {
          notification.success({
            message: "Success",
            description: action?.payload?.meta?.message,
            duration: 2,
          });
        } else {
          notification.error({
            message: "Error",
            description: action?.payload?.meta?.message,
            duration: 2,
          });
        }
      })
      .addCase(googleSigninAuthentication.rejected, (state, action) => {
        state.googleSigninLoading = false;
        state.googleSigninError = action.payload;
      })

      // Google Signup reducers
      .addCase(googleSignupAuthentication.pending, (state) => {
        state.googleSignupLoading = true;
        state.googleSignupError = null;
      })
      .addCase(googleSignupAuthentication.fulfilled, (state, action) => {
        state.googleSignupLoading = false;
        state.googleSignupData = action.payload;
        if (action?.payload?.meta?.success === true) {
          notification.success({
            message: "Success",
            description: action?.payload?.meta?.message,
            duration: 2,
          });
        } else {
          notification.error({
            message: "Error",
            description: action?.payload?.meta?.message,
            duration: 2,
          });
        }
      })
      .addCase(googleSignupAuthentication.rejected, (state, action) => {
        state.googleSignupLoading = false;
        state.googleSignupError = action.payload;
      })

      // LinkedIn Signin reducers
      .addCase(linkedInSigninAuthentication.pending, (state) => {
        state.linkedInSigninLoading = true;
        state.linkedInSigninError = null;
      })
      .addCase(linkedInSigninAuthentication.fulfilled, (state, action) => {
        state.linkedInSigninLoading = false;
        state.linkedInSigninData = action.payload;
        if (action?.payload?.meta?.success === true) {
          notification.success({
            message: "Success",
            description: action?.payload?.meta?.message,
            duration: 2,
          });
        } else {
          notification.error({
            message: "Error",
            description: action?.payload?.meta?.message,
            duration: 2,
          });
        }
      })
      .addCase(linkedInSigninAuthentication.rejected, (state, action) => {
        state.linkedInSigninLoading = false;
        state.linkedInSigninError = action.payload;
      })

      // LinkedIn Signup reducers
      .addCase(linkedInSignupAuthentication.pending, (state) => {
        state.linkedInSignupLoading = true;
        state.linkedInSignupError = null;
      })
      .addCase(linkedInSignupAuthentication.fulfilled, (state, action) => {
        state.linkedInSignupLoading = false;
        state.linkedInSignupData = action.payload;
        if (action?.payload?.meta?.success === true) {
          notification.success({
            message: "Success",
            description: action?.payload?.meta?.message,
            duration: 2,
          });
        } else {
          notification.error({
            message: "Error",
            description: action?.payload?.meta?.message,
            duration: 2,
          });
        }
      })
      .addCase(linkedInSignupAuthentication.rejected, (state, action) => {
        state.linkedInSignupLoading = false;
        state.linkedInSignupError = action.payload;
      });

    // verify accout reducers
    builder
      .addCase(accountVerification.pending, (state) => {
        state.verificationLoading = true;
        state.verificationError = null;
      })
      .addCase(accountVerification.fulfilled, (state, action) => {
        state.verificationLoading = false;
        state.verificationData = action.payload.data;

        const payload = action.payload;
        if (payload.meta?.status === 'ok') {
          state.verificationData = action.payload;
        }
      })
      .addCase(accountVerification.rejected, (state, action) => {
        state.verificationLoading = false;
        state.verificationError = action.payload;

      });


    // resend verification reducers
    builder
      .addCase(resendVerificationEmail.pending, (state) => {
        state.resendVerificationLoading = true;
        state.resendVerificationError = null;
      })
      .addCase(resendVerificationEmail.fulfilled, (state, action) => {
        state.resendVerificationLoading = false;
        state.resendVerificationData = action.payload.data;

        const payload = action.payload;
        if (payload?.meta?.status === 'ok') {
          notification.success({
            message: "Success",
            description: payload?.meta?.message,
            duration: 2,
          });
        } else {
          notification.error({
            message: "Error",
            description: payload?.meta?.message,
            duration: 2,
          });
        }
      })
      .addCase(resendVerificationEmail.rejected, (state, action) => {
        state.resendVerificationLoading = false;
        state.resendVerificationError = action.payload;

      });

  },
});

export const {
  clearSigninData,
  clearSignupData,
  clearForgotPasswordData,
  clearResetPasswordData,
  clearVerificationData,
  clearResendVerificationData,


} = authSlice.actions;
export default authSlice.reducer;